// src/resolvers/programFormResolver.ts

import { showToast } from "@zing/shared/components/toast";
import { FieldErrors } from "react-hook-form";
import { ProgramInputs } from "./inputs";
import { validateTextField } from "./validations/validateTextField";
import { validateVideos } from "./validations/validateVideos";

export const programFormResolver = async (values, context) => {
  const errors: FieldErrors<ProgramInputs> = {};
  const isDraft = context?.isDraft || false;

  /**
   * Nome do programa
   */
  const titleError = validateTextField(values.title, "Nome do programa", {
    required: true,
    minLength: 4,
    maxLength: 140,
  });

  if (titleError) {
    errors.title = {
      type: "manual",
      message: titleError,
    };
  }

  if (!isDraft) {
    if (!values.niches || values.niches.length === 0) {
      errors.niches = {
        type: "manual",
        message: "Segmento/Setor é obrigatório.",
      } as any;
    }

    /**
     * Descrição Curta
     */
    const shortDescriptionError = validateTextField(values.shortDescription, "Descrição curta", {
      required: !isDraft,
      minLength: 4,
      maxLength: 280,
    });

    if (shortDescriptionError) {
      errors.shortDescription = {
        type: "manual",
        message: shortDescriptionError,
      };
    }

    /**
     * Descrição Longa
     */
    const longDescriptionError = validateTextField(values.longDescription, "Descrição longa", {
      required: true,
      minLength: 4,
      maxLength: 5000,
    });

    if (longDescriptionError) {
      errors.longDescription = {
        type: "manual",
        message: longDescriptionError,
      };
    }

    /**
     * Vídeos
     */
    if (!isDraft) {
      const videoErrors = validateVideos(values.videos);

      if (videoErrors) {
        errors.videos = videoErrors;
      }
    }

    /** Seções */

    if (!isDraft) {
      // Verifica se há seções
      if (!values.sections || values.sections.length === 0) {
        // No sections at all – set a top‑level error for the array
        errors.sections = {
          type: "manual",
          message: "Adicione ao menos uma seção.",
        } as any;
      } else {
        // Build a detailed error object matching the shape of `sections`
        const detailedSectionErrors: any[] = [];

        values.sections.forEach((section, sectionIndex) => {
          const secErr: any = {};

          // ---- Section name ----
          if (!section.name) {
            secErr.name = {
              type: "manual",
              message: "Nome da seção é obrigatório.",
            };
          }

          // ---- Questions inside the section ----
          if (section.questions && section.questions.length > 0) {
            const questionErrors: any[] = [];
            section.questions.forEach((question, qIndex) => {
              const qErr: any = {};
              if (!question.name) {
                qErr.name = {type: "manual", message: "Nome da pergunta é obrigatório."};
              }
              if (!question.type) {
                qErr.type = {type: "manual", message: "Tipo da pergunta é obrigatório."};
              }
              if (question.isRequired === undefined) {
                qErr.isRequired = {type: "manual", message: "Obrigatoriedade da pergunta é obrigatória."};
              }
              if (question.isHiddenFromReviwers === undefined) {
                qErr.isHiddenFromReviwers = {type: "manual", message: "Visibilidade da pergunta é obrigatória."};
              }
              // If any error was added for this question, push it at the same index
              if (Object.keys(qErr).length > 0) {
                questionErrors[qIndex] = qErr;
              }
            });
            if (questionErrors.length > 0) {
              secErr.questions = questionErrors;
            }
          }

          // If any error was added for this section, store it at the same index
          if (Object.keys(secErr).length > 0) {
            detailedSectionErrors[sectionIndex] = secErr;
          }
        });

        // Attach the detailed errors only if there is at least one
        if (detailedSectionErrors.length > 0) {
          errors.sections = detailedSectionErrors as any;
        }
      }
    } // end isDraft check for sections

    /** Avaliações */
    if (!isDraft) {
      if (!values.evaluations || values.evaluations.length === 0) {
        errors.evaluations = {
          type: "manual",
          message: "Adicione ao menos uma etapa de avaliação.",
        } as any;
      } else {
        const detailedEvaluationErrors: any[] = [];

        values.evaluations.forEach((evaluation, index) => {
          const evalErr: any = {};

          if (!evaluation.name) {
            evalErr.name = {type: "manual", message: "Nome da etapa é obrigatório."};
          }
          if (!evaluation.type) {
            evalErr.type = {type: "manual", message: "Tipo da avaliação é obrigatório."};
          }
          if (!evaluation.startDate) {
            evalErr.startDate = {type: "manual", message: "Data de início é obrigatória."};
          }
          if (!evaluation.endDate) {
            evalErr.endDate = {type: "manual", message: "Data de término é obrigatória."};
          }

          // Validate that startDate is before endDate (only for first step - Etapa 1)
          if (index === 0 && evaluation.startDate && evaluation.endDate) {
            const start = new Date(evaluation.startDate);
            const end = new Date(evaluation.endDate);
            if (start > end) {
              evalErr.endDate = {
                type: "manual",
                message: "A data de início deve ser igual ou posterior à data de fim, na primeira etapa da avaliação."
              };
            }
          }

          // Special Compliance Criteria
          if (evaluation.type?.value === 'SPECIAL_COMPLIANCE' && evaluation.criteria) {
            const criteriaErrors: any[] = [];
            evaluation.criteria.forEach((criterion, cIndex) => {
              const cErr: any = {};
              if (!criterion.name) {
                cErr.name = {type: "manual", message: "Nome do critério é obrigatório."};
              }
              if (Object.keys(cErr).length > 0) {
                criteriaErrors[cIndex] = cErr;
              }
            });
            if (criteriaErrors.length > 0) {
              evalErr.criteria = criteriaErrors;
            }
          }

          // Performance Sections
          if ((evaluation.type?.value === 'PERFORMANCE_0_5' || evaluation.type?.value === 'PERFORMANCE_1_5') && evaluation.sections) {
            const sectionErrors: any[] = [];
            evaluation.sections.forEach((section, sIndex) => {
              const sErr: any = {};
              if (!section.name) {
                sErr.name = {type: "manual", message: "Nome da seção é obrigatório."};
              }
              if (section.criteria) {
                const criteriaErrors: any[] = [];
                section.criteria.forEach((criterion, cIndex) => {
                  const cErr: any = {};
                  if (!criterion.name) {
                    cErr.name = {type: "manual", message: "Nome do critério é obrigatório."};
                  }
                  if (Object.keys(cErr).length > 0) {
                    criteriaErrors[cIndex] = cErr;
                  }
                });
                if (criteriaErrors.length > 0) {
                  sErr.criteria = criteriaErrors;
                }
              }
              if (Object.keys(sErr).length > 0) {
                sectionErrors[sIndex] = sErr;
              }
            });
            if (sectionErrors.length > 0) {
              evalErr.sections = sectionErrors;
            }
          }

          if (Object.keys(evalErr).length > 0) {
            detailedEvaluationErrors[index] = evalErr;
          }
        });

        if (detailedEvaluationErrors.length > 0) {
          errors.evaluations = detailedEvaluationErrors as any;
        }
      }
    } // end isDraft check for evaluations

    /** Ativo */
    if (!isDraft && !values.isActive) {
      errors.isActive = {
        type: "manual",
        message: "Selecione a visibilidade do programa.",
      };
    }

    /** Imagem de divulgação pequena */
    if (!values.coverImage) {
      errors.coverImage = {
        type: "manual",
        message: "Selecione a imagem de divulgação pequena.",
      };
    }

    /** Imagem de divulgação grande */
    if (!values.homePageSlider) {
      errors.homePageSlider = {
        type: "manual",
        message: "Selecione a imagem de divulgação grande.",
      };
    }

    if (Object.keys(errors).length > 0) {
      showToast('error', 'Existem erros no formulário. Por favor, revise o preenchimento.')
    }
  }

  return {
    values,
    errors,
  };
};
